import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { sweetConfirm } from "../utils/sweetAlerts";

const API = "";
const THUMBNAIL_ROOT = `${API}/uploads/course-thumbnails`;
const rowListWrapStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: "18px",
  background: "#fff",
  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
  overflow: "hidden",
  display: "grid",
  gap: "10px",
  padding: "10px",
};
const rowCardStyle = {
  padding: "10px 12px",
  border: "1px solid rgba(226, 232, 240, 0.95)",
  borderRadius: "14px",
  background: "linear-gradient(180deg, #ffffff 0%, #fcfdff 100%)",
  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
};
const rowLabelStyle = {
  fontSize: "10px",
  lineHeight: 1.4,
  fontWeight: 700,
  color: "#64748b",
  marginBottom: "4px",
  textAlign: "center",
};
const rowValueStyle = {
  fontSize: "13px",
  lineHeight: 1.4,
  color: "#0f172a",
  minWidth: 0,
  textAlign: "center",
};
const rowMutedStyle = {
  fontSize: "11px",
  lineHeight: 1.35,
  color: "#64748b",
  textAlign: "center",
};
const rowCellStyle = {
  minWidth: 0,
  paddingRight: "10px",
  borderRight: "1px solid rgba(226, 232, 240, 0.95)",
  display: "grid",
  gridTemplateRows: "auto 1fr",
  height: "100%",
};
const rowContentStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 0,
  height: "100%",
};

const createSyllabusRow = (semesterNo = 1) => ({
  semester_no: semesterNo,
  semester_title: `Semester ${semesterNo}`,
  module_title: "",
  description: "",
  max_marks: 100,
  practical_max_marks: 0,
  project_max_marks: 0,
  viva_max_marks: 0,
});

function buildSemesterRows(count) {
  return Array.from({ length: count }, (_, index) => ({
    semester_no: index + 1,
    semester_title: `Semester ${index + 1}`,
    items: [createSyllabusRow(index + 1)],
  }));
}

function normalizeSemesters(semesters, count) {
  return Array.from({ length: count }, (_, index) => {
    const semesterNo = index + 1;
    const existing = semesters.find((item) => item.semester_no === semesterNo);
    return existing || {
      semester_no: semesterNo,
      semester_title: `Semester ${semesterNo}`,
      items: [createSyllabusRow(semesterNo)],
    };
  });
}

function flattenSyllabus(semesters) {
  return semesters.flatMap((semester) =>
    semester.items
      .filter((row) => row.module_title.trim() !== "")
      .map((row) => ({
        semester_no: semester.semester_no,
        semester_title: semester.semester_title.trim() || `Semester ${semester.semester_no}`,
        module_title: row.module_title.trim(),
        description: row.description,
        max_marks: Number(row.max_marks || 0),
        practical_max_marks: Number(row.practical_max_marks || 0),
        project_max_marks: Number(row.project_max_marks || 0),
        viva_max_marks: Number(row.viva_max_marks || 0),
      }))
  );
}

function groupCourseSyllabus(rows) {
  const groups = {};
  (rows || []).forEach((row) => {
    const key = Number(row.semester_no || 1);
    if (!groups[key]) {
      groups[key] = {
        semester_no: key,
        semester_title: row.semester_title || `Semester ${key}`,
        items: [],
      };
    }
    groups[key].items.push(row);
  });

  return Object.values(groups).sort((left, right) => left.semester_no - right.semester_no);
}

function semesterTotalMarks(semester) {
  return (semester?.items || []).reduce((sum, item) => sum + totalModuleMarks(item), 0);
}

function totalModuleMarks(item) {
  return Number(item?.max_marks || 0) + Number(item?.practical_max_marks || 0) + Number(item?.project_max_marks || 0) + Number(item?.viva_max_marks || 0);
}

function formatMarks(value) {
  const numericValue = Number(value || 0);
  return Number.isInteger(numericValue) ? String(numericValue) : String(numericValue);
}

function buildSemesterFormFromCourse(course) {
  const semesterCount = Number(course?.semester_count || 1);
  const syllabus = Array.isArray(course?.syllabus) ? course.syllabus : [];
  const grouped = groupCourseSyllabus(syllabus);

  return Array.from({ length: semesterCount }, (_, index) => {
    const semesterNo = index + 1;
    const existing = grouped.find((item) => item.semester_no === semesterNo);
    return {
      semester_no: semesterNo,
      semester_title: existing?.semester_title || `Semester ${semesterNo}`,
      items: existing?.items?.length
        ? existing.items.map((item) => ({
            semester_no: semesterNo,
            semester_title: existing?.semester_title || `Semester ${semesterNo}`,
            module_title: item.module_title || "",
            description: item.description || "",
            max_marks: item.max_marks ?? 100,
            practical_max_marks: item.practical_max_marks ?? 0,
            project_max_marks: item.project_max_marks ?? 0,
            viva_max_marks: item.viva_max_marks ?? 0,
          }))
        : [createSyllabusRow(semesterNo)],
    };
  });
}

function OurCoursesManager() {
  const [meta, setMeta] = useState({ academy_categories: [], models: [], categories: [] });
  const [filters, setFilters] = useState({ academy_category: "", search: "", course_model_id: "", course_category_id: "" });
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewCourse, setPreviewCourse] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1280));
  const [form, setForm] = useState({
    academy_category: "",
    course_model_id: "",
    course_category_id: "",
    title: "",
    description: "",
    duration: "",
    semester_count: 1,
    thumbnail: null,
    wallet_deduction: "",
    tools_points: "",
    gift_card_points: "",
    skill_points: "",
    is_active: true,
    semesters: buildSemesterRows(1),
  });

  const loadMeta = async () => {
    const res = await axios.get(`${API}/api/course-setup/meta`);
    setMeta(res.data || { academy_categories: [], models: [], categories: [] });
  };

  const loadRows = async () => {
    const res = await axios.get(`${API}/api/courses-admin`, { params: filters });
    setRows(res.data || []);
  };

  useEffect(() => {
    Promise.all([loadMeta(), loadRows()]).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const visibleModules = meta.models.filter((item) => !form.academy_category || item.academy_category === form.academy_category);
  const visibleCategories = meta.categories.filter((item) => (!form.academy_category || item.academy_category === form.academy_category) && (!form.course_model_id || String(item.course_model_id) === String(form.course_model_id)));
  const filterModules = meta.models.filter((item) => !filters.academy_category || item.academy_category === filters.academy_category);
  const filterCategories = meta.categories.filter((item) => (!filters.academy_category || item.academy_category === filters.academy_category) && (!filters.course_model_id || String(item.course_model_id) === String(filters.course_model_id)));
  const totalPoints = Number(form.tools_points || 0) + Number(form.gift_card_points || 0) + Number(form.skill_points || 0);

  const totalConfiguredModules = useMemo(
    () => form.semesters.reduce((sum, semester) => sum + semester.items.filter((item) => item.module_title.trim() !== "").length, 0),
    [form.semesters]
  );
  const isMobile = viewportWidth < 768;
  const isTablet = viewportWidth >= 768 && viewportWidth < 1200;
  const rowGridTemplate = isMobile
    ? "1fr"
    : isTablet
      ? "0.7fr 1.2fr 1fr 1.2fr"
      : "auto 1.25fr 1fr 1.2fr auto auto 0.7fr 1fr";

  const updateSemesterCount = (value) => {
    const semesterCount = Math.max(1, Number(value || 1));
    setForm((prev) => ({
      ...prev,
      semester_count: semesterCount,
      semesters: normalizeSemesters(prev.semesters, semesterCount),
    }));
  };

  const updateSemesterTitle = (semesterNo, value) => {
    setForm((prev) => ({
      ...prev,
      semesters: prev.semesters.map((semester) =>
        semester.semester_no === semesterNo ? { ...semester, semester_title: value } : semester
      ),
    }));
  };

  const updateSyllabusRow = (semesterNo, index, key, value) => {
    setForm((prev) => ({
      ...prev,
      semesters: prev.semesters.map((semester) =>
        semester.semester_no !== semesterNo
          ? semester
          : {
              ...semester,
              items: semester.items.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row)),
            }
      ),
    }));
  };

  const addSyllabusRow = (semesterNo) => {
    setForm((prev) => ({
      ...prev,
      semesters: prev.semesters.map((semester) =>
        semester.semester_no !== semesterNo
          ? semester
          : {
              ...semester,
              items: [...semester.items, createSyllabusRow(semesterNo)],
            }
      ),
    }));
  };

  const removeSyllabusRow = (semesterNo, index) => {
    setForm((prev) => ({
      ...prev,
      semesters: prev.semesters.map((semester) =>
        semester.semester_no !== semesterNo
          ? semester
          : {
              ...semester,
              items: semester.items.length === 1 ? semester.items : semester.items.filter((_, rowIndex) => rowIndex !== index),
            }
      ),
    }));
  };

  const saveCourse = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = new FormData();
      payload.append("academy_category", form.academy_category);
      payload.append("course_model_id", form.course_model_id);
      payload.append("course_category_id", form.course_category_id);
      payload.append("title", form.title);
      payload.append("description", form.description);
      payload.append("duration", form.duration);
      payload.append("semester_count", Number(form.semester_count || 1));
      payload.append("wallet_deduction", Number(form.wallet_deduction || 0));
      payload.append("tools_points", Number(form.tools_points || 0));
      payload.append("gift_card_points", Number(form.gift_card_points || 0));
      payload.append("skill_points", Number(form.skill_points || 0));
      payload.append("is_active", form.is_active ? 1 : 0);
      if (form.thumbnail) {
        payload.append("thumbnail", form.thumbnail);
      }
      flattenSyllabus(form.semesters).forEach((row, index) => {
        payload.append(`syllabus[${index}][semester_no]`, row.semester_no);
        payload.append(`syllabus[${index}][semester_title]`, row.semester_title);
        payload.append(`syllabus[${index}][module_title]`, row.module_title);
        payload.append(`syllabus[${index}][description]`, row.description || "");
        payload.append(`syllabus[${index}][max_marks]`, row.max_marks);
        payload.append(`syllabus[${index}][practical_max_marks]`, row.practical_max_marks);
        payload.append(`syllabus[${index}][project_max_marks]`, row.project_max_marks);
        payload.append(`syllabus[${index}][viva_max_marks]`, row.viva_max_marks);
      });
      await axios.post(editingId ? `${API}/api/courses-admin/${editingId}` : `${API}/api/courses-admin`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm({
        academy_category: "",
        course_model_id: "",
        course_category_id: "",
        title: "",
        description: "",
        duration: "",
        semester_count: 1,
        thumbnail: null,
        wallet_deduction: "",
        tools_points: "",
        gift_card_points: "",
        skill_points: "",
        is_active: true,
        semesters: buildSemesterRows(1),
      });
      setEditingId(null);
      await Promise.all([loadMeta(), loadRows()]);
      alert(editingId ? "Course updated successfully." : "Course saved successfully.");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save course.");
    } finally {
      setSaving(false);
    }
  };

  const deleteCourse = async (id) => {
    if (!(await sweetConfirm("Are you sure you want to delete this course?", { confirmButtonText: "Yes, Delete" }))) return;
    try {
      await axios.delete(`${API}/api/courses-admin/${id}`);
      if (editingId === id) {
        setEditingId(null);
        setForm({
          academy_category: "",
          course_model_id: "",
          course_category_id: "",
          title: "",
          description: "",
          duration: "",
          semester_count: 1,
          thumbnail: null,
          wallet_deduction: "",
          tools_points: "",
          gift_card_points: "",
          skill_points: "",
          is_active: true,
          semesters: buildSemesterRows(1),
        });
      }
      await Promise.all([loadMeta(), loadRows()]);
      alert("Course deleted successfully.");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete course.");
    }
  };

  const toggleTrending = async (id) => {
    try {
      await axios.post(`${API}/api/courses-admin/${id}/toggle-trending`);
      await loadRows();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update trending course.");
    }
  };

  return (
    <DashboardLayout>
      <div className="container-fluid" style={{ maxWidth: "1480px" }}>
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body">
            <h3 className="mb-1">Our Courses</h3>
            <p className="text-muted mb-4">Master admin semester-wise course structure ready করবে, আর সেই same structure franchise, frontend, student panel এবং marks approval flow-এ show হবে.</p>

            <form onSubmit={saveCourse}>
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Static Academy Category</label>
                  <select className="form-select" value={form.academy_category} onChange={(e) => setForm((prev) => ({ ...prev, academy_category: e.target.value, course_model_id: "", course_category_id: "" }))} required>
                    <option value="">Select Academy Category</option>
                    {meta.academy_categories.map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Course Module</label>
                  <select className="form-select" value={form.course_model_id} onChange={(e) => setForm((prev) => ({ ...prev, course_model_id: e.target.value, course_category_id: "" }))} required>
                    <option value="">Select Module</option>
                    {visibleModules.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Course Category</label>
                  <select className="form-select" value={form.course_category_id} onChange={(e) => setForm((prev) => ({ ...prev, course_category_id: e.target.value }))} required>
                    <option value="">Select Category</option>
                    {visibleCategories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Course Name</label>
                  <input className="form-control" value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} required />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Duration</label>
                  <input className="form-control" value={form.duration} onChange={(e) => setForm((prev) => ({ ...prev, duration: e.target.value }))} placeholder="6 Months / 1 Year" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Course Thumbnail</label>
                  <input type="file" accept=".jpg,.jpeg,.jfif,.png,.webp" className="form-control" onChange={(e) => setForm((prev) => ({ ...prev, thumbnail: e.target.files?.[0] || null }))} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Total Semesters</label>
                  <input type="number" min="1" max="12" className="form-control" value={form.semester_count} onChange={(e) => updateSemesterCount(e.target.value)} required />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Configured Syllabus</label>
                  <input className="form-control" value={`${totalConfiguredModules} syllabus items`} disabled />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Wallet Deduction</label>
                  <input type="number" className="form-control" value={form.wallet_deduction} onChange={(e) => setForm((prev) => ({ ...prev, wallet_deduction: e.target.value }))} min="0" step="0.01" />
                </div>
                <div className="col-md-9">
                  <label className="form-label">Description</label>
                  <textarea className="form-control" rows="2" value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}></textarea>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Tools Point</label>
                  <input type="number" className="form-control" value={form.tools_points} onChange={(e) => setForm((prev) => ({ ...prev, tools_points: e.target.value }))} min="0" step="0.01" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Gift Card Point</label>
                  <input type="number" className="form-control" value={form.gift_card_points} onChange={(e) => setForm((prev) => ({ ...prev, gift_card_points: e.target.value }))} min="0" step="0.01" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Skill Point</label>
                  <input type="number" className="form-control" value={form.skill_points} onChange={(e) => setForm((prev) => ({ ...prev, skill_points: e.target.value }))} min="0" step="0.01" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Total Point</label>
                  <input className="form-control" value={totalPoints.toFixed(2)} disabled />
                </div>
              </div>

              <div className="mt-4 d-grid gap-3">
                {form.semesters.map((semester) => (
                  <div key={semester.semester_no} className="border rounded-4 p-3 bg-light">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <h5 className="mb-0">Semester {semester.semester_no}</h5>
                        <input
                          className="form-control"
                          style={{ width: "220px" }}
                          value={semester.semester_title}
                          onChange={(e) => updateSemesterTitle(semester.semester_no, e.target.value)}
                        />
                      </div>
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => addSyllabusRow(semester.semester_no)}>Add Syllabus</button>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle mb-0">
                        <thead>
                          <tr>
                            <th style={{ minWidth: "180px" }}>Syllabus Title</th>
                            <th>Description</th>
                            <th style={{ minWidth: "120px" }}>Theory</th>
                            <th style={{ minWidth: "120px" }}>Practical</th>
                            <th style={{ minWidth: "140px" }}>Project / Assignment</th>
                            <th style={{ minWidth: "120px" }}>Viva</th>
                            <th style={{ width: "10%" }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {semester.items.map((row, index) => (
                            <tr key={`${semester.semester_no}-${index}`}>
                              <td><input className="form-control" value={row.module_title} onChange={(e) => updateSyllabusRow(semester.semester_no, index, "module_title", e.target.value)} /></td>
                              <td><input className="form-control" value={row.description} onChange={(e) => updateSyllabusRow(semester.semester_no, index, "description", e.target.value)} /></td>
                              <td><input type="number" className="form-control" value={row.max_marks} onChange={(e) => updateSyllabusRow(semester.semester_no, index, "max_marks", e.target.value)} min="0" step="0.01" /></td>
                              <td><input type="number" className="form-control" value={row.practical_max_marks} onChange={(e) => updateSyllabusRow(semester.semester_no, index, "practical_max_marks", e.target.value)} min="0" step="0.01" /></td>
                              <td><input type="number" className="form-control" value={row.project_max_marks} onChange={(e) => updateSyllabusRow(semester.semester_no, index, "project_max_marks", e.target.value)} min="0" step="0.01" /></td>
                              <td><input type="number" className="form-control" value={row.viva_max_marks} onChange={(e) => updateSyllabusRow(semester.semester_no, index, "viva_max_marks", e.target.value)} min="0" step="0.01" /></td>
                              <td><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeSyllabusRow(semester.semester_no, index)}>Remove</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-3 d-flex align-items-center gap-3 flex-wrap">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" checked={form.is_active} onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))} />
                  <label className="form-check-label">{form.is_active ? "Active" : "Inactive"}</label>
                </div>
                <button className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : editingId ? "Update Course" : "Save Course"}</button>
                {editingId ? (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setEditingId(null);
                      setForm({
                        academy_category: "",
                        course_model_id: "",
                        course_category_id: "",
                        title: "",
                        description: "",
                        duration: "",
                        semester_count: 1,
                        thumbnail: null,
                        wallet_deduction: "",
                        tools_points: "",
                        gift_card_points: "",
                        skill_points: "",
                        is_active: true,
                        semesters: buildSemesterRows(1),
                      });
                    }}
                  >
                    Cancel Edit
                  </button>
                ) : null}
              </div>
            </form>
          </div>
        </div>

        <div className="card shadow-sm border-0">
          <div className="card-body">
            <div className="row g-2 mb-3">
              <div className="col-md-3">
                <select className="form-select" value={filters.academy_category} onChange={(e) => setFilters((prev) => ({ ...prev, academy_category: e.target.value, course_model_id: "", course_category_id: "" }))}>
                  <option value="">All Academy Categories</option>
                  {meta.academy_categories.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <input className="form-control" placeholder="Search course" value={filters.search} onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <select className="form-select" value={filters.course_model_id} onChange={(e) => setFilters((prev) => ({ ...prev, course_model_id: e.target.value, course_category_id: "" }))}>
                  <option value="">All Modules</option>
                  {filterModules.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select" value={filters.course_category_id} onChange={(e) => setFilters((prev) => ({ ...prev, course_category_id: e.target.value }))}>
                  <option value="">All Categories</option>
                  {filterCategories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </div>
              <div className="col-md-1">
                <button className="btn btn-outline-dark w-100" onClick={loadRows}>Go</button>
              </div>
            </div>

            <div style={rowListWrapStyle}>
              {rows.map((item, index) => {
                const semesters = groupCourseSyllabus(item.syllabus);
                return (
                  <div key={item.id} style={rowCardStyle}>
                    <div style={{ display: "grid", gridTemplateColumns: rowGridTemplate, gap: isMobile ? "10px" : "8px", alignItems: "start", width: "100%" }}>
                      <div style={rowCellStyle}>
                        <div style={rowLabelStyle}>Sl No</div>
                        <div style={rowContentStyle}>
                          <div className="fw-semibold" style={rowValueStyle}>{index + 1}</div>
                        </div>
                      </div>

                      <div style={isMobile ? { ...rowCellStyle, borderRight: "none", paddingRight: 0 } : rowCellStyle}>
                        <div style={rowLabelStyle}>Thumbnail</div>
                        <div style={rowContentStyle}>
                          <img
                            src={item.thumbnail ? `${THUMBNAIL_ROOT}/${item.thumbnail}` : "https://placehold.co/220x140/e2e8f0/475569?text=Course"}
                            alt={item.title || "Course thumbnail"}
                            style={{ width: "100%", maxWidth: isMobile ? "100%" : "160px", height: isMobile ? "180px" : "120px", objectFit: "cover", borderRadius: "12px", border: "1px solid #e2e8f0", display: "block" }}
                          />
                        </div>
                      </div>

                      <div style={isMobile || isTablet ? { ...rowCellStyle, borderRight: "none", paddingRight: 0 } : rowCellStyle}>
                        <div style={rowLabelStyle}>Academy Category</div>
                        <div style={rowContentStyle}>
                          <div style={rowValueStyle}>{item.academy_category || "-"}</div>
                        </div>
                      </div>

                      <div style={isMobile ? { ...rowCellStyle, borderRight: "none", paddingRight: 0 } : rowCellStyle}>
                        <div style={rowLabelStyle}>Course Name</div>
                        <div style={{ ...rowContentStyle, flexDirection: "column" }}>
                          <div className="fw-bold" style={{ ...rowValueStyle, fontSize: "14px" }}>{item.title}</div>
                          <div style={{ ...rowMutedStyle, marginTop: "4px" }}>{item.model_name || "-"}</div>
                        </div>
                      </div>

                      <div style={isMobile || isTablet ? { ...rowCellStyle, borderRight: "none", paddingRight: 0 } : rowCellStyle}>
                        <div style={rowLabelStyle}>Fees</div>
                        <div style={rowContentStyle}>
                          <div style={rowValueStyle}>Rs {Number(item.wallet_deduction || 0).toFixed(2)}</div>
                        </div>
                      </div>

                      <div style={isMobile ? { ...rowCellStyle, borderRight: "none", paddingRight: 0 } : rowCellStyle}>
                        <div style={rowLabelStyle}>Syllabus</div>
                        <div style={{ ...rowContentStyle, alignItems: "center" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "center", width: "100%" }}>
                            {semesters.map((semester) => (
                              <div
                                key={`${item.id}-${semester.semester_no}`}
                                style={{
                                  display: "inline-flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "2px",
                                  border: "1px solid #dbeafe",
                                  background: "#eff6ff",
                                  borderRadius: "14px",
                                  padding: "6px 8px",
                                  textAlign: "center",
                                }}
                              >
                                <span className="fw-semibold" style={{ fontSize: "12px", lineHeight: 1.3 }}>{semester.semester_title}</span>
                                <span className="fw-semibold text-primary" style={{ fontSize: "11px", lineHeight: 1.3 }}>Total: {formatMarks(semesterTotalMarks(semester))}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div style={isMobile || isTablet ? { ...rowCellStyle, borderRight: "none", paddingRight: 0 } : rowCellStyle}>
                        <div style={rowLabelStyle}>Trending</div>
                        <div style={rowContentStyle}>
                          <div className="form-check form-switch m-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={Boolean(item.is_trending)}
                              onChange={() => toggleTrending(item.id)}
                              title="Trending Course"
                            />
                          </div>
                        </div>
                      </div>

                      <div style={{ ...rowCellStyle, paddingRight: 0, borderRight: "none" }}>
                        <div style={rowLabelStyle}>Action Button</div>
                        <div style={rowContentStyle}>
                          <div className="d-flex gap-2 flex-wrap justify-content-center" style={{ width: "100%" }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-dark"
                              onClick={() => setPreviewCourse(item)}
                              title="View"
                            >
                              <i className="bi bi-eye"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => {
                                setEditingId(item.id);
                                setForm({
                                  academy_category: item.academy_category || "",
                                  course_model_id: String(item.course_model_id || ""),
                                  course_category_id: String(item.course_category_id || ""),
                                  title: item.title || "",
                                  description: item.description || "",
                                  duration: item.duration || "",
                                  semester_count: Number(item.semester_count || 1),
                                  thumbnail: null,
                                  wallet_deduction: item.wallet_deduction || "",
                                  tools_points: item.tools_points || "",
                                  gift_card_points: item.gift_card_points || "",
                                  skill_points: item.skill_points || "",
                                  is_active: Boolean(item.is_active),
                                  semesters: buildSemesterFormFromCourse(item),
                                });
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }}
                              title="Edit"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteCourse(item.id)} title="Delete">
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {!rows.length ? (
                <div className="text-center text-muted py-4" style={rowCardStyle}>No course found.</div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {previewCourse ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.72)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setPreviewCourse(null)}
        >
          <div
            className="bg-white rounded-4 shadow-lg"
            style={{ width: "min(1100px, 100%)", maxHeight: "calc(100vh - 40px)", overflowY: "auto" }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="p-4 border-bottom d-flex justify-content-between align-items-start gap-3">
              <div>
                <h3 className="mb-1">{previewCourse.title}</h3>
                <div className="text-muted small">
                  {previewCourse.model_name || "-"} | {previewCourse.category_name || "-"} | {Number(previewCourse.semester_count || 1)} semester(s)
                </div>
              </div>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPreviewCourse(null)}>
                Close
              </button>
            </div>

            <div className="p-4 d-grid gap-4">
              <div className="row g-3">
                <div className="col-md-3"><div className="border rounded-3 p-3 h-100"><div className="text-muted small">Wallet Deduction</div><div className="fw-semibold">Rs {Number(previewCourse.wallet_deduction || 0).toFixed(2)}</div></div></div>
                <div className="col-md-3"><div className="border rounded-3 p-3 h-100"><div className="text-muted small">Tools Point</div><div className="fw-semibold">{Number(previewCourse.tools_points || 0).toFixed(2)}</div></div></div>
                <div className="col-md-3"><div className="border rounded-3 p-3 h-100"><div className="text-muted small">Gift Card Point</div><div className="fw-semibold">{Number(previewCourse.gift_card_points || 0).toFixed(2)}</div></div></div>
                <div className="col-md-3"><div className="border rounded-3 p-3 h-100"><div className="text-muted small">Skill Point</div><div className="fw-semibold">{Number(previewCourse.skill_points || 0).toFixed(2)}</div></div></div>
              </div>

              <div>
                <h5 className="mb-2">Description</h5>
                <div className="border rounded-3 p-3 bg-light" style={{ whiteSpace: "pre-wrap" }}>
                  {previewCourse.description || "No description added yet."}
                </div>
              </div>

              <div>
                <h5 className="mb-3">Semester Wise Syllabus</h5>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "14px" }}>
                  {groupCourseSyllabus(previewCourse.syllabus).map((semester) => (
                    <div key={`preview-${previewCourse.id}-${semester.semester_no}`} className="border rounded-4 p-3 bg-light h-100">
                      <div className="fw-bold mb-3">{semester.semester_title}</div>
                      {semester.items.map((module) => (
                        <div key={module.id} className="border rounded-3 bg-white p-2 mb-2">
                          <div className="d-flex justify-content-between align-items-start gap-3">
                            <div className="fw-semibold">{module.module_title}</div>
                            <div className="small fw-semibold text-primary">Total: {formatMarks(totalModuleMarks(module))}</div>
                          </div>
                          <div className="small text-primary mb-1">
                            Theory {formatMarks(module.max_marks)} | Practical {formatMarks(module.practical_max_marks)} | Project {formatMarks(module.project_max_marks)} | Viva {formatMarks(module.viva_max_marks)}
                          </div>
                          <div className="small text-muted mb-1" style={{ whiteSpace: "pre-wrap" }}>{module.description || "-"}</div>
                        </div>
                      ))}
                      <div className="fw-semibold text-primary mt-2">Semester Total: {formatMarks(semesterTotalMarks(semester))}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </DashboardLayout>
  );
}

export default OurCoursesManager;
